import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { lessonProductsService } from "api/services/lesson-products.service";
import ClassEditModal, { TICKET_TYPE_LABELS } from "components/category-group/ClassEditModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import Modal from "components/UI/Modal";
import Tabs from "components/UI/Tabs";

import type { LessonProductListItem, LessonProductDetail } from "api/types/lesson-product.types";
import type { TabItem } from "components/UI/Tabs";
import "./ClassList.scss";
import "pages/AdminPage.scss";

const FILTER_TABS: TabItem[] = [
  { key: "all", label: "전체" },
  { key: "active", label: "판매 중" },
  { key: "inactive", label: "판매 중지" },
];

// ─── Main: ClassList ──────────────────────────────────────────────────────────
const ClassList: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<LessonProductListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("all");
  const [page, setPage] = useState(1);

  const PAGE_SIZE = 20;

  // 편집 모달
  const [editProduct, setEditProduct] = useState<LessonProductDetail | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await lessonProductsService.list();
      setItems(res.data);
    } catch {
      setError("목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // 필터 변경 시 페이지 초기화
  const handleFilterChange = (f: "all" | "active" | "inactive") => {
    setFilterActive(f);
    setPage(1);
  };

  // 검색 변경 시 페이지 초기화
  const handleSearchChange = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  const handleEdit = async (id: number) => {
    try {
      const res = await lessonProductsService.get(id);
      setEditProduct(res.data);
    } catch {
      alert("상세 정보를 불러오는데 실패했습니다.");
    }
  };

  // 클라이언트 사이드 활성/비활성 필터
  const statusFiltered = filterActive === "all" ? items : items.filter((item) => (filterActive === "active" ? item.is_active : !item.is_active));

  // 클라이언트 사이드 검색 필터
  const filtered = search.trim() ? statusFiltered.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()) || item.brand.toLowerCase().includes(search.toLowerCase()) || (item.sport_name || "").toLowerCase().includes(search.toLowerCase())) : statusFiltered;

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="class-list">
      {/* 헤더 */}
      <div className="cl-header">
        <div>
          <h1 className="cl-header__title">수강권 목록</h1>
          <p className="cl-header__sub">총 {filtered.length}개</p>
        </div>
        <Button variant="primary" className="btn btn--lg " onClick={() => navigate("/class-regist")}>
          + 수강권 등록
        </Button>
      </div>

      <div className="admin-page__card">
        {/* 필터 탭 */}
        <Tabs items={FILTER_TABS} activeKey={filterActive} onChange={(k) => handleFilterChange(k as "all" | "active" | "inactive")} />

        {/* 검색 */}
        <div className="admin-page__filter">
          <input className="cl-search" placeholder="강습명, 브랜드, 스포츠로 검색" value={search} onChange={(e) => handleSearchChange(e.target.value)} />
        </div>

        {/* 테이블 */}
        {loading && <p className="cl-status">불러오는 중...</p>}
        {error && <p className="cl-status cl-status--error">{error}</p>}
        {!loading && !error && filtered.length === 0 && <p className="cl-status">수강권이 없습니다.</p>}
        {!loading && filtered.length > 0 && (
          <table className="cl-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>강습명</th>
                <th>브랜드</th>
                <th>스포츠</th>
                <th>정가</th>
                <th>판매가</th>
                <th>세션</th>
                <th>타입</th>
                <th>옵션</th>
                <th>상태</th>
                {/* <th>관리</th> */}
              </tr>
            </thead>
            <tbody>
              {paginated.map((item) => (
                <tr key={item.id} className="cl-row-clickable" onClick={() => handleEdit(item.id)}>
                  <td className="cl-td-id">#{item.id}</td>
                  <td>
                    <div className="cl-product-name">
                      {/* {item.images?.[0]?.image && (
                        <img src={item.images[0].image} alt={item.name} className="cl-thumbnail" />
                      )} */}
                      <span>{item.name}</span>
                    </div>
                  </td>
                  <td>{item.brand}</td>
                  <td>{item.sport_name || item.sport}</td>
                  <td className="cl-td-price">{item.is_discounted && item.discount_price ? <span className="cl-price-original">{Number(item.price).toLocaleString()}원</span> : <span>{Number(item.price).toLocaleString()}원</span>}</td>
                  <td className="cl-td-price">{item.is_discounted && item.discount_price ? <span className="cl-price-sale">{Number(item.discount_price).toLocaleString()}원</span> : <span className="cl-no-option">—</span>}</td>
                  <td>{item.sessions_count}회</td>
                  <td>
                    <span className="cl-ticket-type">{TICKET_TYPE_LABELS[item.allowed_ticket_types] || item.allowed_ticket_types}</span>
                  </td>
                  <td>{item.has_options ? <Badge variant="active">있음</Badge> : <Badge variant="default">없음</Badge>}</td>
                  <td>
                    <Badge variant={item.is_active ? "active" : "default"}>{item.is_active ? "판매 중" : "판매 중지"}</Badge>
                  </td>
                  {/* <td onClick={(e) => e.stopPropagation()}>
                    <div className="cl-actions">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(item.id, item.name)}
                      >
                        삭제
                      </Button>
                    </div>
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* 페이지네이션 */}
        {totalPages > 1 && (
          <div className="cl-pagination">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              이전
            </Button>
            <span className="cl-pagination__info">
              {page} / {totalPages}
            </span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              다음
            </Button>
          </div>
        )}
      </div>

      {/* 편집 모달 */}
      <Modal open={!!editProduct} title={editProduct ? `${editProduct.name}` : ""} width="680px" onClose={() => setEditProduct(null)} closeOnOverlayClick={false}>
        {editProduct && (
          <ClassEditModal
            product={editProduct}
            onSave={() => {
              setEditProduct(null);
              loadList();
            }}
            onClose={() => setEditProduct(null)}
          />
        )}
      </Modal>
    </div>
  );
};

export default ClassList;
